import React, { useState, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import Layout from "../../../layout/Layout";
import SecurityMessage from "../../../sharedComponents/SecurityMessage";
import Translate from "../../../../translate/Translate";
import { useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { PATHS } from "../../../../constants/pathConstants";
import AccountManagementMFAPageStyles from "./AccountManagementMFAPage.styles";
import GLButton from "../../../v2/sharedComponents/GLButton";
import GLButtonV3 from "components/v3/sharedComponents/GLButton";
import ModalWrapper from "components/v2/sharedComponents/Modals/ModalComponents/ModalWrapper";
import { openGLSnackbar } from "../../../../store/reducers/snackbar/openGLSnackbarSlice";
import Mixpanel from "../../../../utils/mixpanel";
import ErasePhoneIcon from "@mui/icons-material/PhonelinkErase";
import { removeCookie } from "utils/v3/CookieUtil";
import {
  enableMfa,
  disableMfa,
  getMemberMfaEnforcedStatus
} from "apis/v3/authApi";

const ContentBoxFrame = (props) => {
  const classes = AccountManagementMFAPageStyles();
  const { upperContent, lowerContent } = props;
  return (
    <Grid
      className={classes.boxFrame}
      container
      xs={11}
      md={6}
      justifyContent="center"
      alignItems="center"
      direction="column"
    >
      <Grid
        item
        className={[classes.contentBoxSection, classes.contentBoxSectionTop]}
      >
        {upperContent}
      </Grid>
      <Grid item className={classes.contentBoxSection}>
        {lowerContent}
      </Grid>
    </Grid>
  );
};

const AccountManagementMFAPage = (props) => {
  const classes = AccountManagementMFAPageStyles();
  const dispatch = useDispatch();
  const { loginWithRedirect } = useAuth0();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isLoadingMfaStatus, setIsLoadingMfaStatus] = useState(true);
  const [isEnablingMfa, setIsEnablingMfa] = useState(false);
  const [isDisablingMfa, setIsDisablingMfa] = useState(false);
  const [reauthDialogIsOpen, setReauthDialogIsOpen] = useState(false);

  // Load MFA status on component mount
  useEffect(() => {
    loadMfaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMfaStatus = async () => {
    try {
      setIsLoadingMfaStatus(true);
      const response = await getMemberMfaEnforcedStatus();
      if (response && !response.isAxiosError) {
        const newMfaStatus =
          response.data?.data?.mfaEnforcedOnLogin === true ||
          response.data?.data?.mfaEnforcedOnLogin === false
            ? response.data?.data?.mfaEnforcedOnLogin
            : false;
        setMfaEnabled(newMfaStatus);
        return newMfaStatus;
      }
      return false;
    } catch (error) {
      Mixpanel.track("Error loading MFA status", { error });
      handleAPICallError();
      return false;
    } finally {
      setIsLoadingMfaStatus(false);
    }
  };

  const handleEnableMfa = async () => {
    setReauthDialogIsOpen(true);
  };

  const handleEnableMfaAndReauth = async () => {
    try {
      setIsEnablingMfa(true);
      Mixpanel.track("MFA enable process started");

      const response = await enableMfa();
      if (response && !response.isAxiosError) {
        const newStatus =
          response.data?.data?.mfaEnforcedOnLogin === true ||
          response.data?.data?.mfaEnforcedOnLogin === false
            ? response.data?.data?.mfaEnforcedOnLogin
            : true;
        setMfaEnabled(newStatus);
        Mixpanel.track("MFA enabled successfully");
      } else {
        throw new Error("Failed to enable MFA");
      }

      setReauthDialogIsOpen(false);

      // Important: clear the existing API token cookie so the callback page
      // actually fetches a new token during this in-session re-login flow.
      removeCookie("accessToken");

      // Force an interactive Auth0 login so Auth0 can prompt for MFA enrollment/challenge.
      loginWithRedirect({
        authorizationParams: {
          prompt: "login",
          max_age: 0
        }
      });
    } catch (error) {
      Mixpanel.track("Error enabling MFA", { error });
      handleAPICallError();
    } finally {
      setIsEnablingMfa(false);
    }
  };

  const handleDisableMfa = async () => {
    try {
      setIsDisablingMfa(true);
      Mixpanel.track("MFA disable process started");

      const response = await disableMfa();

      if (response && !response.isAxiosError) {
        Mixpanel.track("MFA disabled successfully");
        const newStatus =
          response.data?.data?.mfaEnforcedOnLogin === true ||
          response.data?.data?.mfaEnforcedOnLogin === false
            ? response.data?.data?.mfaEnforcedOnLogin
            : false;
        setMfaEnabled(newStatus);
        dispatch(
          openGLSnackbar({
            variant: "success",
            header: "MFA Disabled",
            subText:
              "Multi-factor authentication has been disabled for your account."
          })
        );
      } else {
        throw new Error("Failed to disable MFA");
      }
    } catch (error) {
      Mixpanel.track("Error disabling MFA", { error });
      handleAPICallError();
    } finally {
      setIsDisablingMfa(false);
    }
  };

  const handleAPICallError = () => {
    dispatch(
      openGLSnackbar({
        variant: "error",
        subText:
          "We're so sorry, but something went wrong. Please reach out to us!"
      })
    );
  };

  const resetReauthDialogState = () => {
    setReauthDialogIsOpen(false);
  };

  // Render based on current state
  const renderContent = () => {
    if (isLoadingMfaStatus) {
      return renderLoadingState();
    } else {
      return renderMainState();
    }
  };

  const renderLoadingState = () => {
    return (
      <ContentBoxFrame
        upperContent={
          <Grid item>
            <Typography className={classes.title} variant="deprecated_h6">
              <Translate text="Loading..." />
            </Typography>
          </Grid>
        }
        lowerContent={null}
      />
    );
  };

  const renderMainState = () => {
    const upperContent = (
      <Grid item>
        <Typography className={classes.title} variant="deprecated_h6">
          <Translate text="Additional security measures" />
        </Typography>
        <Typography className={classes.subtext}>
          <Translate
            text="Multi-factor Authentication (MFA) is an additional step at login to help secure your
        personal information."
          />
        </Typography>
      </Grid>
    );
    const renderLowerContent = (
      <>
        <Typography
          className={mfaEnabled ? classes.successSubtext : classes.warnSubtext}
        >
          <Translate
            text={`Text message verification is turned ${
              mfaEnabled ? "ON" : "OFF"
            }`}
          />
        </Typography>
        {mfaEnabled ? (
          <GLButton
            loadingSpinnerWhenClicked
            loading={isDisablingMfa}
            className={classes.continueButton}
            startIcon={<ErasePhoneIcon />}
            onClick={handleDisableMfa}
          >
            Turn text authentication off
          </GLButton>
        ) : (
          <GLButton
            className={classes.continueButton}
            onClick={handleEnableMfa}
          >
            Turn text authentication on
          </GLButton>
        )}
        <Typography
          className={[classes.title, classes.titleGap, classes.titleSmall]}
          variant="deprecated_h6"
        >
          <Translate text="What is it?" />
        </Typography>
        <Typography className={classes.subtext}>
          <Translate
            text="It is a multi-step account login process that requires users to 
          enter more information than just a password. It prevents bad-actors from accessing 
          your account even if they've acquired your log in credentials."
          />
        </Typography>
        <Typography
          className={[classes.title, classes.titleGap, classes.titleSmall]}
          variant="deprecated_h6"
        >
          <Translate text="What do I need to do?" />
        </Typography>
        <Typography className={classes.subtext}>
          <Translate text="To enable MFA, click the button above. You will be prompted to log in again and enter a phone number where you can receive text messages." />
        </Typography>
        <Typography
          className={[classes.title, classes.titleGap, classes.titleSmall]}
          variant="deprecated_h6"
        >
          <Translate text="What if I want to turn it off/on?" />
        </Typography>
        <Typography className={classes.subtext}>
          <Translate text="You can come back to this page to turn this on or off at any time." />
        </Typography>
      </>
    );
    return (
      <ContentBoxFrame
        upperContent={upperContent}
        lowerContent={renderLowerContent}
      />
    );
  };

  return (
    <Layout
      headerType="integrated"
      headerText="Multi-Factor Authentication"
      goBackPath={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_AND_SECURITY}
      goBackTitle={"Privacy & Security"}
    >
      <ModalWrapper
        closeButton={false}
        closeOnBackdropClick={true}
        isOpen={reauthDialogIsOpen}
        onClose={resetReauthDialogState}
        modalContainerSx={{
          flexBasis: {
            xs: "90% !important"
          },
          maxHeight: "85vh",
          maxWidth: {
            xs: "95%",
            lg: "805px !important"
          },
          overflow: "auto",
          padding: {
            xs: "32px 16px 24px 16px !important",
            lg: "40px 32px 32px 32px !important"
          },
          margin: "auto",
          outline: 0
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%"
          }}
        >
          <Typography
            className={classes.title}
            variant="deprecated_h6"
            sx={{ textAlign: "left" }}
          >
            <Translate text="Text message verification" />
          </Typography>

          <Typography className={classes.subtext}>
            <Translate text="Please note: to finish enabling MFA, you’ll be asked to log in again." />
          </Typography>
          <Typography className={classes.subtext}>
            <Translate text="During that login, you’ll be prompted to enter a valid phone number. We only allow US numbers at this time. This will be the phone number we send codes to, so please keep this phone handy now and any time you log in." />
          </Typography>
          <Typography className={classes.subtext}>
            <Translate text="When you press Continue, you’ll be redirected to complete setup and may receive a verification code via text message." />
          </Typography>

          <Typography
            className={[classes.title, classes.titleSmall]}
            variant="deprecated_h6"
            sx={{ marginTop: 1 }}
          >
            <Translate text="What do we need this phone number for?" />
          </Typography>
          <Typography className={classes.subtext}>
            <Translate text="We will only use this phone number to authenticate your identity when you log in. We will not use this phone number for anything except authentication unless you also have the same phone number set as your contact phone number under your account settings. Changing that number will not change this number." />
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: { xs: 1, sm: 2 },
              justifyContent: "flex-end",
              marginTop: 2
            }}
          >
            <GLButtonV3
              buttonType="secondary"
              onClick={resetReauthDialogState}
              disabled={isEnablingMfa}
              sx={{
                flex: "1 1 0",
                width: "100%",
                maxWidth: "none"
              }}
            >
              <Typography variant="body_strong_primary">
                <Translate text="Cancel" />
              </Typography>
            </GLButtonV3>
            <GLButtonV3
              buttonType="primary"
              onClick={handleEnableMfaAndReauth}
              loading={isEnablingMfa}
              disabled={isEnablingMfa}
              sx={{
                flex: "1 1 0",
                width: "100%",
                maxWidth: "none"
              }}
            >
              <Typography variant="body_strong_white">
                <Translate text="Continue" />
              </Typography>
            </GLButtonV3>
          </Box>
        </Box>
      </ModalWrapper>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        {renderContent()}

        <Grid>
          <Grid item xs={12}>
            <SecurityMessage
              title="Your information is safe with us"
              desc="Genomic Life uses SSL encryption to protect your information."
            />
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
};
export default AccountManagementMFAPage;
